/**
 * src/features/admin/ui/EmptyStates.tsx
 *
 * Forbidden and Error empty-state cards for AdminMetricsPage (ADMIN-01).
 * Extracted from AdminMetricsPage to keep page component under 200 lines (CLAUDE.md rule 5).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldOff, AlertCircle } from 'lucide-react';

interface ErrorStateProps {
  onRetry: () => void;
}

export function ForbiddenState(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center px-4">
      <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-4 text-red-400">
        <ShieldOff size={32} aria-hidden="true" />
      </div>
      <h2 className="text-subhead font-bold text-slate-900 dark:text-slate-50 mb-2">
        {t('admin.not_authorized.title', 'Not authorized')}
      </h2>
      <p className="text-body text-slate-500 dark:text-slate-400 max-w-xs">
        {t('admin.not_authorized.body', 'This page is for administrators only.')}
      </p>
    </div>
  );
}

export function ErrorState({ onRetry }: ErrorStateProps): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center px-4">
      <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-4 text-slate-400">
        <AlertCircle size={32} aria-hidden="true" />
      </div>
      <h2 className="text-subhead font-bold text-slate-900 dark:text-slate-50 mb-2">
        {t('admin.error.title', 'Could not load metrics')}
      </h2>
      <p className="text-body text-slate-500 dark:text-slate-400 max-w-xs mb-6">
        {t('admin.error.body', 'An unexpected error occurred. Try refreshing.')}
      </p>
      <button
        onClick={onRetry}
        className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-button font-bold text-sm transition-colors"
      >
        {t('admin.error.retry', 'Retry')}
      </button>
    </div>
  );
}
